'use client';

import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Eye, EyeClosed, Plus, Wifi, X } from 'lucide-react';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import { useBlockModalWifiStore } from '@/stores/useBlockModalWifiStore';
import { type WifiProfile, useBlockWifiStore } from '@/stores/useBlockWifiStore';
import IconAdaptive from '@/components/IconAdaptive';

const EMPTY_FORM = () => ({ networkName: '', ssid: '', password: '' });

const MAX_SSID_BYTES = 32;
const MAX_PASSWORD_LENGTH = 63;
const MAX_NETWORK_NAME_LENGTH = 100;

const MOCK_SCAN_RESULTS: { ssid: string; strength: 1 | 2 | 3 | 4 }[] = [
    { ssid: 'HomeNetwork_5G', strength: 4 },
    { ssid: 'SchooMyOffice', strength: 4 },
    { ssid: 'SchooMyOffice_5G', strength: 3 },
    { ssid: 'iPhone_Guest', strength: 3 },
    { ssid: 'Neighbor_WiFi', strength: 2 },
    { ssid: 'BUFFALO-A1B2C3', strength: 2 },
    { ssid: 'elecom-abc123', strength: 2 },
    { ssid: 'aterm-xxxx-g', strength: 1 },
    { ssid: 'aterm-xxxx-a', strength: 1 },
    { ssid: 'docomo_Wi-Fi', strength: 1 },
    { ssid: 'au_Wi-Fi2', strength: 1 },
    { ssid: 'SoftBank_WiFi', strength: 1 },
];

function getSsidByteLength(value: string) {
    return new TextEncoder().encode(value).length;
}

function WifiSignalIcon({ strength }: { strength: 1 | 2 | 3 | 4 }) {
    const bars = [1, 2, 3, 4];
    return (
        <div className="flex items-end gap-[2px] h-3">
            {bars.map((b) => (
                <div
                    key={b}
                    className="w-[3px] rounded-sm"
                    style={{
                        height: `${b * 25}%`,
                        backgroundColor: b <= strength ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.2)',
                    }}
                />
            ))}
        </div>
    );
}

export default function WifiModal() {
    const { closeModal, setOnConfirm } = useBlockModalStore();
    const { fieldRef } = useBlockModalWifiStore();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const { profiles, addProfile, updateProfile, removeProfile, setProfiles } = useBlockWifiStore();

    const [isLoading, setIsLoading] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(() => fieldRef?.getProfile()?.id ?? null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [newForm, setNewForm] = useState(EMPTY_FORM);
    const [addError, setAddError] = useState('');
    const [editError, setEditError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        if (!isAuthenticated) return;
        setIsLoading(true);
        apiFetch('/boards/editor/wifi')
            .then((data) => {
                const mapped: WifiProfile[] = (data.profiles ?? []).map((p: any) => ({
                    id: p.id,
                    networkName: p.network_name,
                    ssid: p.ssid,
                    password: p.password,
                }));
                setProfiles(mapped);
            })
            .catch(() => {})
            .finally(() => setIsLoading(false));
    }, [isAuthenticated]);

    useEffect(() => {
        if (!selectedId) {
            setForm(EMPTY_FORM());
            return;
        }
        const profile = profiles.find((p) => p.id === selectedId);
        if (profile) {
            setForm({ networkName: profile.networkName, ssid: profile.ssid, password: profile.password });
        }
        setIsDirty(false);
        setEditError('');
    }, [selectedId, profiles]);

    const handleSelect = (profile: WifiProfile) => {
        setSelectedId(profile.id);
        setAddError('');
    };

    const handleFormChange = (key: keyof typeof form, value: string) => {
        setForm((f) => ({ ...f, [key]: value }));
        setIsDirty(true);
        setEditError('');
    };

    const validateForm = (): string => {
        if (getSsidByteLength(form.ssid) > MAX_SSID_BYTES) {
            return `SSIDは${MAX_SSID_BYTES}バイト以内で入力してください`;
        }
        if (form.password.length > MAX_PASSWORD_LENGTH) {
            return `パスワードは${MAX_PASSWORD_LENGTH}文字以内で入力してください`;
        }
        if (form.networkName.length > MAX_NETWORK_NAME_LENGTH) {
            return `ネットワーク名は${MAX_NETWORK_NAME_LENGTH}文字以内で入力してください`;
        }
        return '';
    };

    const handleFormBlur = () => {
        if (!selectedId || !isDirty) return;
        const error = validateForm();
        if (error) {
            setEditError(error);
            return;
        }
        updateProfile(selectedId, form);
        setIsDirty(false);
    };

    const handleAdd = async () => {
        if (!newForm.ssid.trim()) {
            setAddError('SSIDを入力してください');
            return;
        }
        if (getSsidByteLength(newForm.ssid.trim()) > MAX_SSID_BYTES) {
            setAddError(`SSIDは${MAX_SSID_BYTES}バイト以内で入力してください`);
            return;
        }
        const dup = profiles.find((p) => p.ssid === newForm.ssid.trim());
        if (dup) {
            setAddError('同じSSIDのプロファイルが既に存在します');
            return;
        }

        const profile = {
            networkName: newForm.ssid.trim(),
            ssid: newForm.ssid.trim(),
            password: '',
        };

        if (isAuthenticated) {
            try {
                const data = await apiFetch('/boards/editor/wifi', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        network_name: profile.networkName,
                        ssid: profile.ssid,
                        password: profile.password,
                    }),
                });
                const newProfile: WifiProfile = {
                    id: data.id,
                    networkName: data.network_name,
                    ssid: data.ssid,
                    password: '',
                };
                addProfile(newProfile);
                setSelectedId(newProfile.id);
            } catch {
                setAddError('登録に失敗しました');
                return;
            }
        } else {
            const id = crypto.randomUUID();
            addProfile({ id, ...profile });
            setSelectedId(id);
        }

        setNewForm(EMPTY_FORM());
        setAddError('');
    };

    const handleRemove = async (id: string) => {
        if (isAuthenticated) {
            apiFetch(`/boards/editor/wifi/${id}`, { method: 'DELETE' }).catch(() => {});
        }
        removeProfile(id);
        if (selectedId === id) {
            setSelectedId(null);
            setForm(EMPTY_FORM());
            setEditError('');
        }
    };

    const handleOk = useCallback(async () => {
        if (fieldRef) {
            if (selectedId) {
                const profile = profiles.find((p) => p.id === selectedId);
                if (profile) {
                    const error = validateForm();
                    if (error) {
                        setEditError(error);
                        return;
                    }
                    const networkName = form.networkName.trim() || form.ssid || profile.ssid;
                    const finalProfile = { ...profile, networkName, ssid: form.ssid, password: form.password };
                    if (isAuthenticated) {
                        await apiFetch(`/boards/editor/wifi/${selectedId}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                network_name: networkName,
                                ssid: form.ssid,
                                password: form.password,
                            }),
                        }).catch(() => {});
                    }
                    updateProfile(selectedId, { networkName, ssid: form.ssid, password: form.password });
                    fieldRef.setProfile(finalProfile);
                }
            } else {
                fieldRef.setProfile(null);
            }
        }
        closeModal();
    }, [fieldRef, selectedId, profiles, form, isAuthenticated, updateProfile, closeModal]);

    useEffect(() => {
        setOnConfirm(handleOk);
    }, [handleOk, setOnConfirm]);

    const scanList = (
        <div className="overflow-y-auto flex-1">
            {MOCK_SCAN_RESULTS.map((ap, i) => (
                <button
                    key={ap.ssid}
                    onClick={() => {
                        setNewForm((f) => ({ ...f, ssid: ap.ssid, networkName: ap.ssid }));
                        setAddError('');
                    }}
                    className={`flex items-center justify-between w-full px-4 py-2 text-xs transition-colors hover:bg-color-block-wifi-modal-header/20 ${
                        i !== 0 ? 'border-t border-white/10' : ''
                    } ${newForm.ssid === ap.ssid ? 'bg-color-block-wifi-modal-header/30 text-white' : 'text-white/70'}`}
                >
                    <span>{ap.ssid}</span>
                    <WifiSignalIcon strength={ap.strength} />
                </button>
            ))}
        </div>
    );

    return (
        <div className="flex flex-col h-full bg-color-block-action-modal-bg rounded-2xl overflow-hidden">
            {/* ヘッダー */}
            <div className="flex items-center px-3 h-[50px] flex-shrink-0 bg-color-block-wifi-modal-header">
                <span className="mr-2">
                    <IconAdaptive Icon={Wifi} strokeWidth={2} baseSize={28} className="text-white" />
                </span>
                <span className="text-color-white-base font-semibold text-sm">Wi-Fi設定</span>
            </div>

            {/* 上段：登録済みプロファイル一覧 */}
            <div className="px-4 pt-3 pb-3 border-b border-color-block-function-modal-button-border flex-shrink-0 min-h-[80px]">
                <p className="text-color-block-action-modal-text-muted text-xs mb-2">Wi-Fi 選択</p>
                <div className="flex gap-3 flex-wrap items-center">
                    {isLoading && <p className="text-white/30 text-xs">読み込み中...</p>}
                    <button
                        onClick={() => {
                            setSelectedId(null);
                            setForm(EMPTY_FORM());
                            setEditError('');
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0 ${
                            selectedId === null
                                ? 'bg-color-block-wifi-modal-header text-white'
                                : 'bg-color-block-function-modal-button-bg-light text-white/70 hover:text-white'
                        }`}
                    >
                        接続しない
                    </button>
                    {profiles.map((profile) => (
                        <div key={profile.id} className="relative flex-shrink-0">
                            <button
                                onClick={() => handleSelect(profile)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    selectedId === profile.id
                                        ? 'bg-color-block-wifi-modal-header text-white'
                                        : 'bg-color-block-function-modal-button-bg-light text-white/70 hover:text-white'
                                }`}
                            >
                                {profile.networkName}
                            </button>
                            <button
                                onClick={() => handleRemove(profile.id)}
                                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-color-block-variable-modal-button-bg border border-color-block-variable-modal-button-border flex items-center justify-center text-white/40 hover:text-red-400 transition-colors"
                            >
                                <IconAdaptive Icon={X} strokeWidth={2} baseSize={10} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* 中央 */}
            <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
                <AnimatePresence initial={false}>
                    {selectedId && (
                        <motion.div
                            key="selected-form"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.25, ease: 'easeInOut' }}
                            className="overflow-hidden flex-shrink-0"
                        >
                            <div className="px-4 pt-3 pb-24 flex flex-col gap-3">
                                <p className="text-color-block-action-modal-text-muted text-xs">選択中のWi-Fi</p>
                                <div className="flex gap-3">
                                    <div className="flex flex-col gap-1.5 flex-1">
                                        <label className="text-color-block-action-modal-text-muted text-xs">SSID</label>
                                        <input
                                            value={form.ssid}
                                            inputMode="url"
                                            autoComplete="off"
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setForm((f) => ({
                                                    ...f,
                                                    ssid: val,
                                                    networkName: f.networkName === f.ssid ? val : f.networkName,
                                                }));
                                                setIsDirty(true);
                                                setEditError('');
                                            }}
                                            onBlur={handleFormBlur}
                                            className="bg-color-block-variable-modal-button-bg text-color-white-base border border-white/20 rounded px-3 py-1.5 text-sm outline-none focus:border-color-block-wifi-modal-header w-full"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5 flex-1">
                                        <label className="text-color-block-action-modal-text-muted text-xs">
                                            パスワード
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={form.password}
                                                inputMode="url"
                                                autoComplete="off"
                                                onChange={(e) => handleFormChange('password', e.target.value)}
                                                onBlur={handleFormBlur}
                                                style={!showPassword ? { fontFamily: 'sans-serif' } : undefined}
                                                className="bg-color-block-variable-modal-button-bg text-color-white-base border border-white/20 rounded px-3 py-1.5 text-sm outline-none focus:border-color-block-wifi-modal-header w-full pr-12"
                                            />
                                            <button
                                                onClick={() => setShowPassword((v) => !v)}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                                            >
                                                <IconAdaptive
                                                    Icon={showPassword ? EyeClosed : Eye}
                                                    strokeWidth={1.5}
                                                    baseSize={16}
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                {editError && <p className="text-red-400 text-xs">{editError}</p>}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* 周辺のWi-Fi一覧 */}
                <motion.div layout className="flex flex-col min-h-0 flex-1 overflow-hidden border-t border-white/10">
                    <p className="text-color-block-action-modal-text-muted text-xs px-4 py-2 flex-shrink-0">
                        周辺のWi-Fi
                    </p>
                    {scanList}
                </motion.div>
            </div>

            {/* フッター */}
            <div className="flex justify-between items-start gap-2 px-5 py-3 border-t border-color-block-variable-modal-button-border flex-shrink-0">
                <div className="flex flex-col gap-1">
                    <div className="flex gap-2 items-center">
                        <input
                            className="bg-color-block-variable-modal-button-bg text-color-white-base border border-white/20 rounded px-3 py-1.5 text-sm outline-none focus:border-color-block-wifi-modal-header placeholder:text-white/30 w-40"
                            placeholder="SSID"
                            value={newForm.ssid}
                            inputMode="url"
                            autoComplete="off"
                            onChange={(e) => {
                                setNewForm((f) => ({ ...f, ssid: e.target.value, networkName: e.target.value }));
                                setAddError('');
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAdd();
                            }}
                        />
                        <button
                            onClick={handleAdd}
                            className={`flex items-center gap-1 px-3 py-1.5 bg-color-block-variable-modal-button-bg border border-dashed border-white/30 rounded-lg text-white/50 text-xs hover:border-color-block-wifi-modal-header hover:text-color-block-wifi-modal-header transition-colors flex-shrink-0 ${
                                newForm.ssid.length > 0 ? 'animate-pulse' : ''
                            }`}
                            style={newForm.ssid.length > 0 ? { borderColor: '#1565C0', color: '#1565C0' } : {}}
                        >
                            <IconAdaptive Icon={Plus} strokeWidth={2} baseSize={14} className="mr-1" />
                            Wi-Fi追加
                        </button>
                    </div>
                    {addError && <p className="text-red-400 text-xs">{addError}</p>}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                    <button
                        onClick={closeModal}
                        className="px-4 py-2 bg-color-block-variable-modal-cancel text-white/70 rounded-lg text-sm"
                    >
                        キャンセル
                    </button>
                    <button
                        onClick={handleOk}
                        className="px-6 py-2 bg-color-block-wifi-modal-ok text-color-white-base rounded-lg text-sm font-semibold hover:bg-color-block-wifi-modal-ok-hover transition-colors"
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
}
